import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Avatar,
  Chip
} from '@mui/material';
import { apiClient } from '../../../utils/ApiClient';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';

interface RegisterEmailTemplateProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dataSets: any[];
  getDisplayCollectionName: (name?: string) => string;
  apiEndpointPrefix?: string;
  initialTemplateContent?: string;
  initialEmailSubject?: string;
  initialEmailBuilderConfig?: any;
  preselectedSystemTemplateId?: string | null;
}

interface EmailTemplateData {
  templateName: string;
  templateType: string;
  accessLevel: string;
  emailSubject: string;
  dataSet: string;
  category: string;
  keywords: string[];
}

export const RegisterEmailTemplate: React.FC<RegisterEmailTemplateProps> = ({
  open,
  onClose,
  onSuccess,
  dataSets,
  getDisplayCollectionName,
  apiEndpointPrefix,
  initialTemplateContent,
  initialEmailSubject,
  initialEmailBuilderConfig,
  preselectedSystemTemplateId,
}) => {
  const endpointBase = apiEndpointPrefix || '/iu/contenttemplate';
  const user = useSelector((state: RootState) => state.authentication.user);
  
  const [formData, setFormData] = useState<EmailTemplateData>({
    templateName: '',
    templateType: 'EMAIL',
    accessLevel: 'Public',
    emailSubject: initialEmailSubject || '',
    dataSet: '',
    category: '',
    keywords: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const [copyFromOptions, setCopyFromOptions] = useState<any[]>([]);
  const [selectedSystemTemplate, setSelectedSystemTemplate] = useState<any>(null);

  useEffect(() => {
    if (open) {
      const fetchCopyOptions = async () => {
        try {
          const [sysRes, userRes] = await Promise.all([
            apiClient.get('/iu/systemtemplate/catalog/mail').catch(() => ({ data: [] })),
            apiClient.get('/iu/contenttemplate/company/mail').catch(() => ({ data: [] })),
          ]);
          const sysTemplates = (Array.isArray(sysRes.data) ? sysRes.data : []).map((t: any) => ({ ...t, _source: 'system' }));
          const userTemplates = (Array.isArray(userRes.data) ? userRes.data : [])
            .filter((t: any) => t.isApproved === true)
            .map((t: any) => ({ ...t, _source: 'user' }));
          const merged = [...sysTemplates, ...userTemplates];
          setCopyFromOptions(merged);
          if (preselectedSystemTemplateId) {
            const match = merged.find((t: any) => t._id === preselectedSystemTemplateId);
            if (match) {
              setSelectedSystemTemplate(match);
              setFormData(prev => ({
                ...prev,
                emailSubject: match.emailSubject || prev.emailSubject,
                category: match.category || '',
                keywords: match.keywords?.length ? [...match.keywords] : [],
              }));
              return;
            }
          }
          setSelectedSystemTemplate(null);
        } catch {
          setCopyFromOptions([]);
          setSelectedSystemTemplate(null);
        }
      };
      fetchCopyOptions();
    } else {
      setCopyFromOptions([]);
      setSelectedSystemTemplate(null);
    }
  }, [open, preselectedSystemTemplateId]);

  const getTemplateDisplayName = (name: string) => {
    if (!name) return '';
    const lower = name.toLowerCase();
    if (lower.startsWith('iust-')) return name.substring(5);
    if (lower.startsWith('iut-')) {
      const parts = name.split('-');
      return parts.length >= 3 ? parts.slice(2).join('-') : name;
    }
    return name;
  };

  useEffect(() => {
    if (open && initialEmailSubject) {
      setFormData(prev => ({ ...prev, emailSubject: initialEmailSubject }));
    }
  }, [open, initialEmailSubject]);

  const handleInputChange = (field: keyof EmailTemplateData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.value;
    
    // Special validation for template name - only alphanumeric
    if (field === 'templateName') {
      const sanitizedValue = value.replace(/[^a-zA-Z0-9_-]/g, '');
      setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const validateForm = (): boolean => {
    if (!formData.templateName.trim()) {
      setError('Template Name is required');
      return false;
    }
    if (!formData.emailSubject.trim()) {
      setError('Email Subject is required');
      return false;
    }
    if (!formData.dataSet) {
      setError('Data Set is required');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const sysTemplate = selectedSystemTemplate;
      const templateContent = sysTemplate?.templateContent || initialTemplateContent || '<p>Email template content will be created using the Email Builder.</p>';
      const builderConfig = sysTemplate?.emailBuilderConfig || initialEmailBuilderConfig;
      const emailSubject = formData.emailSubject || sysTemplate?.emailSubject || '';

      const payload: any = {
        templateName: formData.templateName,
        templateType: 'mail',
        accessLevel: formData.accessLevel.toLowerCase(),
        emailSubject,
        collectionId: formData.dataSet,
        templateContent,
        ...(builderConfig ? { emailBuilderConfig: builderConfig } : {}),
        ...(formData.category ? { category: formData.category } : {}),
        ...(formData.keywords.length ? { keywords: formData.keywords } : {}),
        sharedTo: [],
        mediaAttachments: []
      };
      if (!apiEndpointPrefix) {
        payload.companyId = user.companyId;
        payload.isApprovalRequired = true;
        payload.isApproved = false;
      }
      const result = await apiClient.post(`${endpointBase}/new`, payload);

      console.log('Email template created successfully:', result);
      
      setFormData({
        templateName: '',
        templateType: 'EMAIL',
        accessLevel: 'Public',
        emailSubject: '',
        dataSet: '',
        category: '',
        keywords: [],
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography component="span" variant="h6">
          Create New Email Template
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {copyFromOptions.length > 0 && (
            <Box>
              <Autocomplete
                size="small"
                options={[{ _id: '__none__', templateName: 'None', _source: '' }, ...copyFromOptions]}
                groupBy={(option: any) => option._id === '__none__' ? '' : option._source === 'system' ? 'System Templates' : 'My Templates'}
                getOptionLabel={(option: any) =>
                  option._id === '__none__' ? 'None' : getTemplateDisplayName(option.templateName)
                }
                value={selectedSystemTemplate}
                onChange={(_, value: any) => {
                  if (!value || value._id === '__none__') {
                    setSelectedSystemTemplate(null);
                    setFormData(prev => ({ ...prev, category: '', keywords: [] }));
                    return;
                  }
                  setSelectedSystemTemplate(value);
                  setFormData(prev => ({
                    ...prev,
                    emailSubject: value.emailSubject || prev.emailSubject,
                    category: value.category || '',
                    keywords: value.keywords?.length ? [...value.keywords] : [],
                  }));
                }}
                isOptionEqualToValue={(opt: any, val: any) => opt._id === val?._id}
                renderOption={(props, option: any) => (
                  <li {...props} key={option._id}>
                    <Box display="flex" alignItems="center" gap={1.5} py={0.5} width="100%">
                      {option._id !== '__none__' && option.thumbnailUrl ? (
                        <Box
                          component="img"
                          src={option.thumbnailUrl}
                          sx={{ width: 60, height: 60, borderRadius: 1, objectFit: 'cover', flexShrink: 0, border: '1px solid', borderColor: 'divider' }}
                        />
                      ) : option._id !== '__none__' ? (
                        <Avatar variant="rounded" sx={{ width: 60, height: 60, fontSize: '0.7rem', bgcolor: 'action.selected' }}>
                          N/A
                        </Avatar>
                      ) : null}
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option._id === '__none__' ? 'None' : getTemplateDisplayName(option.templateName)}
                      </Typography>
                      {option._id !== '__none__' && (
                        <Chip
                          label={option._source === 'system' ? 'System' : 'User'}
                          size="small"
                          color={option._source === 'system' ? 'info' : 'success'}
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Copy from Template" />}
              />
              {selectedSystemTemplate?.thumbnailUrl && (
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Box
                    component="img"
                    src={selectedSystemTemplate.thumbnailUrl}
                    alt={getTemplateDisplayName(selectedSystemTemplate.templateName)}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          <TextField
            label="Template Name"
            value={formData.templateName}
            onChange={handleInputChange('templateName')}
            fullWidth
            required
            helperText="Only alphanumeric characters, underscores and hyphens allowed"
            disabled={loading}
          />

          <TextField
            label="Template Type"
            value={formData.templateType}
            fullWidth
            disabled
          />

          <FormControl fullWidth required>
            <InputLabel id="access-level-label">Access Level</InputLabel>
            <Select
              labelId="access-level-label"
              value={formData.accessLevel}
              label="Access Level"
              onChange={handleInputChange('accessLevel')}
              disabled={loading}
            >
              <MenuItem value="Public">Public</MenuItem>
              <MenuItem value="Private">Private</MenuItem>
            </Select>
          </FormControl>

          <TextField
            select
            size="small"
            label="Category"
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            fullWidth
            disabled={loading}
            SelectProps={{
              displayEmpty: true,
              renderValue: (v: unknown) => {
                const val = v as string;
                if (!val) return <Typography variant="body2" color="text.secondary">Select category</Typography>;
                const cats = [
                  { value: 'transactional', label: 'Transactional' },
                  { value: 'promotional', label: 'Promotional' },
                  { value: 'service', label: 'Service' },
                  { value: 'system', label: 'System' },
                ];
                return cats.find(c => c.value === val)?.label ?? val;
              },
            }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value="transactional">Transactional</MenuItem>
            <MenuItem value="promotional">Promotional</MenuItem>
            <MenuItem value="service">Service</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </TextField>

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={formData.keywords}
            onChange={(_e: any, newValue: string[]) => setFormData(prev => ({ ...prev, keywords: newValue }))}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
            disabled={loading}
          />

          <TextField
            label="Email Subject"
            value={formData.emailSubject}
            onChange={handleInputChange('emailSubject')}
            fullWidth
            required
            disabled={loading}
          />

          <FormControl fullWidth required>
            <Autocomplete
              size="small"
              options={dataSets}
              getOptionLabel={(option) => getDisplayCollectionName(option?.collectionName)}
              value={dataSets.find((ds) => ds._id === formData.dataSet) || null}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  dataSet: newValue?._id || ''
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Data Set" />}
              disabled={loading}
            />
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          color="secondary"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="success"
          disabled={loading || !formData.templateName || !formData.emailSubject || !formData.dataSet}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterEmailTemplate;
